/**
 * DmsOverflowCorrector.ts
 * Corrector de overflow en coordenadas DMS de documentos PTEL.
 *
 * Detecta y corrige minutos ≥60 y segundos ≥60, propagando
 * el overflow en cascada (seg→min→deg).
 *
 * Caso real: conversiones erróneas en ~50-60% de documentos escaneados.
 *
 * @module DmsOverflowCorrector
 * @version 1.0.0
 * @date 2026-02-08
 * @task F2.04
 */

// ============================================================================
// TIPOS
// ============================================================================

/** Componentes DMS parseados. */
export interface DmsComponents {
  degrees: number;
  minutes: number;
  seconds: number;
  hemisphere?: 'N' | 'S' | 'E' | 'W';
}

/** Resultado de la corrección DMS. */
export interface DmsOverflowResult {
  readonly corrected: true;
  readonly original: string;
  readonly normalized: string;
  readonly components: DmsComponents;
  readonly overflowType: 'minutes' | 'seconds' | 'cascade' | 'double';
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Valores absurdos que deben rechazarse directamente. */
const MAX_REASONABLE_MINUTES = 999;
const MAX_REASONABLE_SECONDS = 999;
const MAX_DEGREES = 360;

// ============================================================================
// REGEX DMS
// ============================================================================

/**
 * Captura DMS con minutos/segundos potencialmente ≥60.
 * Grupos: (1)degrees (2)minutes (3)seconds? (4)hemisphere?
 * Acepta ° ' " y variantes Unicode.
 */
const DMS_OVERFLOW_RE =
  /^(-?\d+)\s*[°º]\s*(\d+(?:\.\d+)?)\s*['''′]?\s*(?:(\d+(?:\.\d+)?)\s*["""″]?)?\s*([NSEW])?[-]?$/i;

// ============================================================================
// LÓGICA CORE
// ============================================================================

/**
 * Normaliza overflow en componentes DMS in-place.
 * Retorna true si hubo algún overflow, false si ya era válido.
 */
function normalizeOverflow(c: DmsComponents): 'minutes' | 'seconds' | 'cascade' | 'double' | null {
  const hadMinOverflow = c.minutes >= 60;
  const hadSecOverflow = c.seconds >= 60;

  if (!hadMinOverflow && !hadSecOverflow) return null;

  // Paso 1: corregir segundos
  if (c.seconds >= 60) {
    const extraMin = Math.floor(c.seconds / 60);
    c.seconds = round(c.seconds % 60, 4);
    c.minutes += extraMin;
  }

  // Paso 2: corregir minutos (puede haber crecido por paso 1)
  if (c.minutes >= 60) {
    const extraDeg = Math.floor(c.minutes / 60);
    c.minutes = round(c.minutes % 60, 4);
    c.degrees += extraDeg;
  }

  // Clasificar tipo de overflow
  if (hadMinOverflow && hadSecOverflow) return 'double';
  if (hadSecOverflow && c.minutes >= 0) {
    // Si los segundos desbordaron Y eso causó que minutos también desbordaran
    // eso es cascada. Pero si minutos ya eran <60 antes del desborde de seg,
    // entonces solo es 'seconds'.
    return hadMinOverflow ? 'cascade' : (c.degrees > 0 ? 'cascade' : 'seconds');
  }
  if (hadSecOverflow) return 'seconds';
  return 'minutes';
}

/** Redondeo a n decimales. */
function round(v: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}

/** Formatea componentes DMS como cadena legible. */
function formatDms(c: DmsComponents): string {
  const hemi = c.hemisphere ?? '';
  const minInt = Math.trunc(c.minutes);
  const minPad = String(minInt).padStart(2, '0');

  // Si minutos tienen parte decimal, convertir a segundos adicionales
  const minFrac = c.minutes - minInt;
  const totalSec = round(c.seconds + minFrac * 60, 4);

  if (Number.isInteger(totalSec)) {
    const secPad = String(totalSec).padStart(2, '0');
    return `${c.degrees}°${minPad}'${secPad}"${hemi}`;
  }
  // Segundos decimales: no pad, mantener decimales
  return `${c.degrees}°${minPad}'${totalSec}"${hemi}`;
}

// ============================================================================
// API PÚBLICA
// ============================================================================

/**
 * Detecta y corrige overflow DMS en una cadena de coordenadas.
 *
 * @param input - Coordenada DMS con posible overflow (ej: "37°65'12\"N")
 * @returns Resultado con la coordenada corregida, o null si no hay overflow o input inválido
 */
export function correctDmsOverflow(input: string): DmsOverflowResult | null {
  const trimmed = input.trim();
  const match = DMS_OVERFLOW_RE.exec(trimmed);
  if (!match) return null;

  const degrees = parseInt(match[1], 10);
  const minutes = parseFloat(match[2]);
  const seconds = match[3] !== undefined ? parseFloat(match[3]) : 0;
  const hemisphere = (match[4]?.toUpperCase() as DmsComponents['hemisphere']) || undefined;

  // Rechazar valores absurdos
  if (degrees >= MAX_DEGREES || minutes >= MAX_REASONABLE_MINUTES || seconds >= MAX_REASONABLE_SECONDS) {
    return null;
  }

  // Si no hay overflow, no hay nada que corregir
  if (minutes < 60 && seconds < 60) return null;

  const components: DmsComponents = { degrees, minutes, seconds, hemisphere };
  const overflowType = normalizeOverflow(components);

  if (overflowType === null) return null;

  return {
    corrected: true,
    original: trimmed,
    normalized: formatDms(components),
    components,
    overflowType,
  };
}

/**
 * Detección rápida: ¿tiene la cadena DMS overflow?
 */
export function hasDmsOverflow(input: string): boolean {
  return correctDmsOverflow(input) !== null;
}
